/**
 * Custom Site Specific Javascript
 *
 * This file is to add custom features to the website without having to modify
 * core/common assets. Define a custom class, and build new or overwrite
 * existing methods to create custom functionality.
 */

import { modal } from "commonAssets/lib/modal";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";

/**
 * Initializes a claim object.
 *
 * @param {Object} claim - The claim object to be initialized.
 * @returns {Object} The initialized claim object.
 */
export function customInitClaim(claim) {
    console.log("[Initiate claim]...");

    disableIfHasValue("first_name");
    disableIfHasValue("last_name");
    initTransactionRowRequiredState();
    moveFlatCashPaymentSection();

    const addTransactionsButton = document.getElementById("add_transactions_1");
    const eligibilityField = document.getElementById(
        "eligibility_financial_losses"
    );

    initAddTransactionsButton(addTransactionsButton);

    if (eligibilityField) {
        eligibilityField.addEventListener("change", () => {
            syncFinancialLossesState(eligibilityField, addTransactionsButton);
        });
        syncFinancialLossesState(eligibilityField, addTransactionsButton);
    }

    return claim;
}

function disableIfHasValue(id) {
    const field = document.getElementById(id);
    if (!field) return;

    field.toggleAttribute("disabled", Boolean((field.value || "").trim()));
}

function initTransactionRowRequiredState() {
    if (!document.body.dataset.patelcoTransactionRowRequiredInit) {
        document.addEventListener("input", handleTransactionRowRequiredState);
        document.addEventListener("change", handleTransactionRowRequiredState);
        document.body.dataset.patelcoTransactionRowRequiredInit = "true";
    }

    const rowIds = new Set(
        Array.from(document.querySelectorAll("[item-id]")).map((field) =>
            field.getAttribute("item-id")
        )
    );

    rowIds.forEach((rowId) => syncTransactionRowRequiredState(rowId));
}

function handleTransactionRowRequiredState(event) {
    const rowId = event?.target?.getAttribute?.("item-id");
    if (!rowId) return;

    syncTransactionRowRequiredState(rowId);
}

function syncTransactionRowRequiredState(rowId) {
    const rowFields = Array.from(
        document.querySelectorAll(`[item-id="${rowId}"]`)
    ).filter(isTransactionInput);

    if (!rowFields.length) return;

    const shouldBeRequired = rowFields.some(hasUserValue);

    rowFields.forEach((field) => {
        field.toggleAttribute("required", shouldBeRequired);
    });
}

function isTransactionInput(field) {
    if (!field?.tagName) return false;

    return ["INPUT", "SELECT", "TEXTAREA"].includes(field.tagName);
}

function hasUserValue(field) {
    if (field.type === "checkbox" || field.type === "radio") {
        return field.checked;
    }

    return Boolean((field.value || "").trim());
}

function moveFlatCashPaymentSection() {
    const documentation = document.getElementById("documentation");
    if (!documentation) return;

    let claimFieldsContainer = document.getElementById("claim-fields-cont");

    if (!claimFieldsContainer) {
        claimFieldsContainer = document.createElement("div");
        claimFieldsContainer.id = "claim-fields-cont";
        claimFieldsContainer.className =
            "bg-light text-dark border rounded p-3 mb-5";
        documentation.insertAdjacentElement("afterend", claimFieldsContainer);
    }

    const sectionNodes = [
        document.querySelector('[data-field-id="__wrapper_4"]'),
        document.querySelector('[data-field-id="__wrapper_5"]'),
        document.querySelector('[data-field-id="__wrapper_6"]'),
        document.querySelector('[data-field-id="_flat_cash_payment_election"]'),
    ].filter(Boolean);

    sectionNodes.forEach((node) => {
        claimFieldsContainer.appendChild(node);
    });
}

function initAddTransactionsButton(button) {
    if (!button || button.dataset.patelcoDisabledGuardInit === "true") return;

    const guardDisabledActivation = (event) => {
        if (button.getAttribute("aria-disabled") !== "true") return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
    };

    button.addEventListener("click", guardDisabledActivation, true);
    button.addEventListener("keydown", (event) => {
        if (!["Enter", " ", "Spacebar"].includes(event.key)) return;

        guardDisabledActivation(event);
    });
    button.dataset.patelcoDisabledGuardInit = "true";
}

function syncFinancialLossesState(eligibilityField, addTransactionsButton) {
    const allowDocumentedLosses = eligibilityField?.value === "YES";

    document.querySelectorAll("[item-id]").forEach((field) => {
        if (allowDocumentedLosses) {
            field.toggleAttribute("disabled", false);
            return;
        }

        field.value = "";
        toggleFieldError(field, false);
        field.toggleAttribute("disabled", true);
    });

    if (!addTransactionsButton) return;

    addTransactionsButton.setAttribute(
        "aria-disabled",
        allowDocumentedLosses ? "false" : "true"
    );
    addTransactionsButton.classList.toggle("disabled", !allowDocumentedLosses);
    addTransactionsButton.tabIndex = allowDocumentedLosses ? 0 : -1;
}

/**
 * Custom function to load a member.
 *
 * @param {Object} claim - The member object to be loaded.
 * @returns {Object} - The loaded member object.
 */
export function customLoadMember(claim) {
    console.log("[Load class member]...");
    // custom code goes here
    const addTransactionsButton = document.getElementById("add_transactions_1");
    const eligibilityField = document.getElementById(
        "eligibility_financial_losses"
    );

    initAddTransactionsButton(addTransactionsButton);

    if (eligibilityField) {
        syncFinancialLossesState(eligibilityField, addTransactionsButton);
    }

    if (claim.data.claim_id && claim.data.files.length > 0) {
        claim.prev_docs = true;
    }
    return claim;
}

/**
 * Processes a claim for a custom member.
 *
 * @param {Object} claim - The claim to be processed.
 * @returns {Object} The processed claim.
 */
export function customProcessClaim(claim) {
    console.log("[Process claim]...");

    let transactionCount = 0;
    document.querySelectorAll('[item-id]').forEach(field => {
        if (isTransactionInput(field) && hasUserValue(field)) {
            transactionCount++;
        }
    });
    if (transactionCount == 0 && !document.getElementById('flat_cash_payment_election_0').checked) {
        modal("Error", "Please provide documented loss information in Section II or elect for the Flat Cash Payment in Section III.");
        claim.flags.valid = false;
    }
    if (transactionCount > 0 && document.getElementById('flat_cash_payment_election_0').checked) {
        modal("Error", "You cannot elect for the Flat Cash Payment if you are requesting reimbursement for documented losses. Please uncheck the Flat Cash Payment election or remove the documented loss information.");
        claim.flags.valid = false;
    }
    if (transactionCount > 0 && claim.uploads.length == 0 && !claim.prev_docs) {
        modal("Error", "Please upload supporting documentation for your losses in Section II.");
        claim.flags.valid = false;
    }
    return claim;
}

/**
 * Appends the Patelco IVR hours below the existing phone number on the
 * contact page without changing the shared contact renderer.
 *
 * @returns {void}
 */
export function appendContactHours() {
    const phoneRow = document.getElementById("adminPhone");
    if (!phoneRow || phoneRow.querySelector(".patelco-contact-hours")) return;

    phoneRow.insertAdjacentHTML(
        "afterend",
        [
            '<span class="mt-2 d-block patelco-contact-hours">',
            "<strong>Hours:</strong> 24/7",
            "</span>",
        ].join("")
    );
}

/**
 * Groups Spanish-language important documents beneath a separate subheader
 * using the repo-standard CMS `--es` title flag.
 *
 * @returns {void}
 */
export function groupSpanishDocuments() {
    const container = document.querySelector(".important-documents-container");
    if (!container) return;

    const docList = container.querySelector(".doc-list");
    if (!docList || docList.querySelector("#patelco-spanish-docs")) return;

    const spanishDocs = [];
    const docItems = Array.from(docList.querySelectorAll("li.doc-item"));

    docItems.forEach((item) => {
        const link = item.querySelector("a.doc-link");
        if (!link) return;

        const title = (link.textContent || "").trim();
        if (!/--\s*es\b/i.test(title)) return;

        const cleanedTitle = title.replace(/\s*--\s*es\b/i, "").trim();
        const icon = document.createElement("i");
        icon.className = "bi bi-file-earmark-pdf text-danger me-2";

        link.replaceChildren(icon, document.createTextNode(cleanedTitle));

        docList.removeChild(item);
        spanishDocs.push(item);
    });

    if (!spanishDocs.length) return;

    const section = document.createElement("li");
    section.id = "patelco-spanish-docs";
    section.className = "doc-item doc-item--subheader mt-4 mb-2";

    const heading = document.createElement("h3");
    heading.className = "h3";
    heading.textContent = "Documentos importantes";
    section.appendChild(heading);
    docList.appendChild(section);

    spanishDocs.forEach((item) => docList.appendChild(item));
}
